import { useState, useEffect, useRef, useCallback } from 'react';
import { useDialogStore } from '@/store/dialogStore';
import clsx from 'clsx';

export function DialogProvider() {
  const { isOpen, type, options, close } = useDialogStore();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && type === 'prompt') {
      setInputValue(options?.defaultValue ?? '');
    }
  }, [isOpen, type, options?.defaultValue]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        if (type === 'prompt' && inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        } else if (confirmRef.current) {
          confirmRef.current.focus();
        }
      });
    }
  }, [isOpen, type]);

  const handleConfirm = useCallback(() => {
    if (type === 'prompt') {
      close(inputValue);
    } else {
      close(true);
    }
  }, [type, inputValue, close]);

  const handleCancel = useCallback(() => {
    if (type === 'prompt') {
      close(null);
    } else {
      close(false);
    }
  }, [type, close]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleCancel();
    } else if (e.key === 'Enter' && type === 'prompt') {
      e.preventDefault();
      handleConfirm();
    }
  }, [handleCancel, handleConfirm, type]);

  if (!isOpen || !options) return null;

  const isDanger = options.variant === 'danger';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onKeyDown={handleKeyDown}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={handleCancel} />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-sm bg-surface-secondary border border-border-primary rounded-xl shadow-2xl animate-scale-in mx-4"
      >
        <div className="px-5 pt-5 pb-4">
          <h3 className="text-[14px] font-semibold text-text-primary">{options.title}</h3>
          <p className="text-[12px] text-text-tertiary mt-1.5 whitespace-pre-line">{options.message}</p>

          {type === 'prompt' && (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={options.placeholder}
              className="w-full mt-3 px-3 py-2 text-[13px] rounded-lg border border-border-primary bg-surface-primary text-text-primary placeholder-text-quaternary outline-none focus:border-accent-violet transition-colors"
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 pb-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-3.5 py-1.5 text-[12px] font-medium rounded-lg text-text-secondary hover:bg-surface-hover transition-colors"
          >
            {options.cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={handleConfirm}
            className={clsx(
              'px-3.5 py-1.5 text-[12px] font-medium rounded-lg transition-colors',
              isDanger
                ? 'bg-accent-rose text-white hover:bg-accent-rose/80'
                : 'bg-accent-violet text-white hover:bg-accent-violet-light'
            )}
          >
            {options.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
